/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Progress,
  Icon,
  Tooltip,
  HStack,
} from '@chakra-ui/react';
import { Warning, WarningCircle, XCircle, TrendDown } from 'phosphor-react';
import { ITaxRegime } from '../../../../../hooks/useCompanies';
import { formatCNPJ, toCurrency } from '../../../../../utils/FormatFunctions';
import { getProgressColor } from '../../../../../utils/HandleFunctions';

// Função para detectar alertas da empresa
const getCompanyAlerts = (company: ITaxRegime) => {
  const alerts: Array<{
    type: 'error' | 'warning' | 'info';
    icon: typeof Warning;
    message: string;
    color: string;
  }> = [];

  // Limite excedido (100%+)
  if (company.limite_atingido >= 100) {
    alerts.push({
      type: 'error',
      icon: XCircle,
      message: 'Limite do regime excedido!',
      color: 'red',
    });
  }
  // Limite próximo (80%+)
  else if (company.limite_atingido >= 80) {
    alerts.push({
      type: 'warning',
      icon: WarningCircle,
      message: 'Próximo do limite do regime',
      color: 'orange',
    });
  }

  // Faturamento muito baixo
  if (company.faturamento_acumulado > 0 && company.faturamento_acumulado < 1000) {
    alerts.push({
      type: 'info',
      icon: TrendDown,
      message: 'Faturamento muito baixo',
      color: 'yellow',
    });
  }

  // Dados inconsistentes
  if (!company.faixa_atual || company.faixa_atual === 'N/A') {
    alerts.push({
      type: 'warning',
      icon: Warning,
      message: 'Regime tributário não identificado',
      color: 'purple',
    });
  }

  return alerts;
};

interface TaxRegimeTableProps {
  taxRegimeList: ITaxRegime[];
  isLoading: boolean;
  currentRegime: string | null;
  // eslint-disable-next-line no-unused-vars
  onRowClick: (item: ITaxRegime) => void;
}

export function TaxRegimeTable({
  taxRegimeList,
  isLoading,
  currentRegime,
  onRowClick,
}: TaxRegimeTableProps) {
  if (isLoading && taxRegimeList?.length === 0) {
    return (
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Box>
    );
  }

  if (!isLoading && taxRegimeList?.length === 0) {
    return (
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Flex justify="center" align="center" h="400px">
          <Text color="gray.500">Nenhuma empresa encontrada</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th w="50px">Status</Th>
              <Th w="60px">ID</Th>
              <Th>Empresa</Th>
              {currentRegime === 'simples_nacional' && <Th w="80px">Regime</Th>}
              <Th isNumeric>Faturamento Acumulado</Th>
              <Th isNumeric w="200px">
                % do Limite
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {taxRegimeList?.map(item => {
              const alerts = getCompanyAlerts(item);
              const hasError = alerts.some(a => a.type === 'error');
              const hasWarning = alerts.some(a => a.type === 'warning');

              return (
                <Tr
                  key={item.external_id}
                  _hover={{ bg: 'gray.50' }}
                  cursor="pointer"
                  onClick={() => onRowClick(item)}
                  bg={(() => {
                    if (hasError) return 'red.50';
                    if (hasWarning) return 'orange.50';
                    return 'white';
                  })()}
                >
                  <Td>
                    <HStack spacing={1}>
                      {alerts.length > 0 ? (
                        alerts.map(alert => (
                          <Tooltip
                            key={`${alert.type}-${alert.color}`}
                            label={alert.message}
                            hasArrow
                            placement="top"
                            bg={`${alert.color}.600`}
                          >
                            <Box display="flex">
                              <Icon
                                as={alert.icon}
                                boxSize={4}
                                color={`${alert.color}.500`}
                                weight="fill"
                              />
                            </Box>
                          </Tooltip>
                        ))
                      ) : (
                        <Badge colorScheme="green" fontSize="2xs" px={1}>
                          OK
                        </Badge>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      {item.external_id}
                    </Text>
                  </Td>
                  <Td>
                    <Box>
                      <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                        {item.razao_social?.length > 40
                          ? `${item.razao_social.substring(0, 40)}...`
                          : item.razao_social}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatCNPJ(item.cnpj)}
                      </Text>
                    </Box>
                  </Td>
                  {currentRegime === 'simples_nacional' && (
                    <Td>
                      <Badge colorScheme="purple" fontSize="2xs" px={1}>
                        {item.regime_tributario}
                      </Badge>
                    </Td>
                  )}
                  <Td isNumeric>
                    <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                      {toCurrency(item.faturamento_acumulado)}
                    </Text>
                  </Td>
                  <Td isNumeric>
                    <Flex direction="column" gap={1} align="flex-end">
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={
                          item.limite_atingido >= 80
                            ? 'red.500'
                            : item.limite_atingido >= 60
                              ? 'orange.500'
                              : 'green.500'
                        }
                      >
                        {item.limite_atingido}%
                      </Text>
                      <Progress
                        value={item.limite_atingido}
                        size="xs"
                        colorScheme={getProgressColor(item.limite_atingido)}
                        w="100px"
                        borderRadius="full"
                      />
                    </Flex>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
